import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { useUser } from "../../core/context/user";
import { loadBtwLastUrl } from "../../core/btw-last-url";
import { adminFetchBoards } from "../../core/axios/endpoints";
import { isSubsidiePartner } from "../../core/partner-subsidie";

// Jira-achtige linker navigatie. Alle bestaande pagina's blijven bereikbaar;
// alleen de indeling (groepen) en de looks zijn nieuw.

const TILE_COLORS = ["#0C66E4", "#1F845A", "#8F7EE7", "#E56910", "#AE2A19", "#206A83", "#5E4DB2", "#943D73"];
const tileColor = (key = "") => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return TILE_COLORS[Math.abs(h) % TILE_COLORS.length];
};

const NavItem = ({ to, icon, label, active, badge, tile }) => (
  <li className={`nav-item ${active ? "active" : ""}`}>
    <Link to={to} title={label}>
      {tile ? (
        <span className="board-tile" style={{ background: tile.color }}>
          {tile.text}
        </span>
      ) : (
        <FeatherIcon icon={icon} />
      )}
      <span className="nav-label">{label}</span>
      {badge != null ? <span className="nav-badge">{badge}</span> : null}
    </Link>
  </li>
);

// Sectiekop; met `to` is de kop zelf een link (bv. Borden -> overzicht).
const SectionTitle = ({ children, to, active }) => (
  <li className={`nav-section-title ${active ? "is-active" : ""}`}>
    {to ? (
      <Link to={to} className="nav-section-link" title="Overzicht">
        <span>{children}</span>
        <FeatherIcon icon="chevron-right" size={12} />
      </Link>
    ) : (
      <span>{children}</span>
    )}
  </li>
);

const Sidebar = () => {
  const { isCurrentUserAdmin, userData } = useUser();
  const isBtwPartner = !!userData?.btwPartnerEnabled;
  const isSubsidiePartnerUser = isSubsidiePartner(userData);
  const location = useLocation();
  const pathname = location.pathname;
  const isAdmin = isCurrentUserAdmin();

  // Laatst bezochte BTW-url (lijst of board) en laatst bezochte bord.
  const [btwLastUrl, setBtwLastUrl] = useState(loadBtwLastUrl());
  useEffect(() => {
    const refreshBtw = () => setBtwLastUrl(loadBtwLastUrl());
    window.addEventListener("storage", refreshBtw);
    window.addEventListener("btw:lastUrl", refreshBtw);
    return () => {
      window.removeEventListener("storage", refreshBtw);
      window.removeEventListener("btw:lastUrl", refreshBtw);
    };
  }, []);

  // Borden (één per klant + Direct); ververst na wijzigingen via "boards:changed".
  const [boards, setBoards] = useState([]);
  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    const load = () =>
      adminFetchBoards()
        .then((res) => !cancelled && setBoards(res?.data ?? res ?? []))
        .catch(() => {});
    load();
    window.addEventListener("boards:changed", load);
    return () => {
      cancelled = true;
      window.removeEventListener("boards:changed", load);
    };
  }, [isAdmin]);

  // Mobiel menu sluiten bij navigatie (bestaand gedrag).
  useEffect(() => {
    document.querySelector(".main-wrapper")?.classList.remove("slide-nav");
    const overlay = document.querySelector(".sidebar-overlay");
    if (overlay) {
      overlay.classList.remove("opened");
      overlay.onclick = function () {
        this.classList.remove("opened");
        document.querySelector(".main-wrapper")?.classList.remove("slide-nav");
      };
    }
  }, [pathname]);

  const is = (fragment) => pathname.includes(fragment);
  // Type-bord (Aanvragen-sectie): /borden/alle?type=...
  const typeActive = pathname.endsWith("/borden/alle") ? new URLSearchParams(location.search).get("type") ?? "" : "";

  if (isAdmin) {
    const btwActive = is("btw-batterij") && !is("btw-batterij-instellingen");
    return (
      <div className="sidebar" id="sidebar">
        <div className="sidebar-inner">
          <ul>
            <NavItem to="/panel/admin/dashboard" icon="home" label="Dashboard" active={is("/admin/dashboard")} />

            <SectionTitle to="/panel/admin/borden" active={pathname === "/panel/admin/borden"}>Borden per klant</SectionTitle>
            {boards.filter((b) => b.kind !== "direct" || (b.counts?.open ?? 0) > 0).map((b) => (
              <NavItem
                key={b._id}
                to={`/panel/admin/borden/${b._id}`}
                label={b.name}
                active={is(`/borden/${b._id}`)}
                badge={b.counts?.open || null}
                tile={{ text: (b.key ?? "?").slice(0, 3), color: b.kind === "direct" ? "#44546F" : tileColor(b.key) }}
              />
            ))}

            <SectionTitle>Aanvragen</SectionTitle>
            <NavItem to="/panel/admin/borden/alle?type=btw" icon="battery-charging" label="BTW Batterij" active={typeActive === "btw" || btwActive} />
            <NavItem to="/panel/admin/borden/alle?type=eia" icon="zap" label="EIA" active={typeActive === "eia" || is("eia-overzicht")} />
            <NavItem to="/panel/admin/borden/alle?type=eiaZon" icon="sun" label="EIA Zon" active={typeActive === "eiaZon" || is("eia-zon-overzicht")} />
            <NavItem to="/panel/admin/borden/alle?type=isde" icon="thermometer" label="ISDE" active={typeActive === "isde" || is("isde-overzicht")} />

            <SectionTitle>Facturatie</SectionTitle>
            <NavItem to="/panel/admin/financieel" icon="credit-card" label="Financieel" active={is("financieel")} />
            <NavItem to="/panel/admin/partner-facturen" icon="file-text" label="Partner-facturen" active={is("partner-facturen")} />

            <SectionTitle>Beheer</SectionTitle>
            <NavItem to="/panel/admin/klanten" icon="users" label="Klanten" active={is("klanten")} />
            <NavItem to="/panel/admin/btw-batterij-instellingen" icon="columns" label="Statussen" active={is("btw-batterij-instellingen")} />
            <NavItem to="/panel/admin/btw-labels" icon="tag" label="Labels" active={is("btw-labels")} />
            <NavItem to="/panel/admin/btw-email-templates" icon="mail" label="Mail-templates" active={is("btw-email-templates")} />
            <NavItem to="/panel/admin/verstuurde-mails" icon="send" label="Verstuurde mails" active={is("verstuurde-mails")} />
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar" id="sidebar">
      <div className="sidebar-inner">
        <ul>
          <NavItem to="/panel/customer/dashboard" icon="home" label="Dashboard" active={is("dashboard")} />

          <SectionTitle>Aanvragen</SectionTitle>
          <NavItem to="/panel/customer/eia-aanvragen" icon="zap" label="EIA Aanvragen" active={is("eia-aanvragen")} />
          <NavItem to="/panel/customer/eia-zon-aanvragen" icon="sun" label="EIA Zon Aanvragen" active={is("eia-zon-aanvragen")} />
          <NavItem to="/panel/customer/isde-aanvragen" icon="thermometer" label="ISDE Aanvragen" active={is("isde-aanvragen")} />
          {isBtwPartner ? (
            <NavItem to="/panel/customer/btw-teruggaves" icon="battery-charging" label="BTW Teruggaves" active={is("btw-teruggaves")} />
          ) : null}
          {isSubsidiePartnerUser ? (
            <NavItem to="/panel/customer/partner-subsidie-aanvragen" icon="briefcase" label="Subsidie-aanvragen" active={is("partner-subsidie-aanvragen")} />
          ) : null}

          <SectionTitle>Facturatie</SectionTitle>
          <NavItem to="/panel/customer/facturen" icon="file-text" label="Facturen" active={is("facturen")} />
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
